<template>
  <hr-form-dialog
    ref="dialog"
    size="sm"
    no-refocus
    :title="$t(isEditing ? 'tableViews.dialogEditTitle' : 'tableViews.dialogSaveTitle')"
    @hide="onDialogHide"
    @submit="onOkClick"
  >
    <div class="column q-gutter-y-md">
      <hr-p
        v-if="!isEditing"
        class="q-mb-none"
      >
        {{ saveMessageText }}
      </hr-p>
      <hr-text-field
        v-model="viewTitle"
        autofocus
        lazy-rules
        hide-bottom-space
        data-e2e="input-view-name"
        :label="$t('tableViews.dialogTitleLabel')"
        :rules="[(val) => !!val?.trim() || $t('tableViews.dialogValidateTitle')]"
      />
      <div v-if="canSaveGlobal">
        <div class="row items-center q-mb-xs hr-label-large hr-text-on-surface-variant">
          {{ $t('tableViews.visibilityText') }}
          <hr-icon-hint-btn
            v-if="!canEditViewVisibility"
            class="q-ml-xs"
            :hint="$t('tableViews.visibilityInfoTooltip')"
          />
        </div>
        <div class="column q-gutter-y-sm">
          <template v-if="canEditViewVisibility">
            <hr-radio
              v-model="editedShareType"
              :val="TABLE_VIEW_SHARED_TYPES.PRIVATE"
              :label="$t('tableViews.dialogPrivateLabel')"
              data-e2e="radio-visibility-private"
            />
            <hr-radio
              v-model="editedShareType"
              :val="TABLE_VIEW_SHARED_TYPES.GLOBAL"
              :label="$t('tableViews.dialogGlobalLabel')"
              data-e2e="radio-visibility-global"
            />
            <hr-radio
              v-model="editedShareType"
              :val="TABLE_VIEW_SHARED_TYPES.ROLE_SPECIFIC"
              :label="$t('tableViews.dialogSpecificLabel')"
              data-e2e="radio-visibility-role-specific"
            />
          </template>
          <hr-p
            v-else
            class="q-mb-none"
          >
            {{ currentVisibilityTitle }}
          </hr-p>
        </div>
        <hr-multi-select
          v-if="editedShareType === TABLE_VIEW_SHARED_TYPES.ROLE_SPECIFIC"
          v-model="selectedRoles"
          :label="$t('tableViews.roleSelectText')"
          :options="userRoleOptions"
          lazy-rules
          hide-bottom-space
          :rules="[(val) => !!val.length || $t('tableViews.dialogValidateSpecificRoles')]"
          class="q-mt-md"
        />
      </div>
    </div>
    <template #destructive-actions>
      <hr-destructive-btn
        v-if="isEditing && (isExistingAsPrivate || isManager)"
        data-e2e="button-delete-view"
        :label="$t('common.buttonDelete')"
        @click="onDeleteClick"
      />
    </template>
    <template #actions>
      <hr-dismiss-btn
        data-e2e="button-cancel"
        :label="$t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        type="submit"
        data-e2e="button-save"
        :disable="isSaveDisabled"
        :label="$t('common.buttonSave')"
      />
    </template>
  </hr-form-dialog>
</template>
<script>
import { mapState } from 'pinia';
import { TABLE_VIEW_SHARED_TYPES } from 'src/constants/tableView';
import {
  alertDialog,
  confirmDestructiveDialog,
  HrConfirmBtn,
  HrDestructiveBtn,
  HrDismissBtn,
  HrFormDialog,
  HrIconHintBtn,
  HrMultiSelect,
  HrP,
  HrRadio,
  HrTextField,
} from 'src/design-system';
import { auth } from 'src/store/auth';
import { getExistingShareType } from 'src/utils/TableViewUtils';

export default {
  name: 'SaveTableViewDialog',

  components: {
    HrConfirmBtn,
    HrDestructiveBtn,
    HrDismissBtn,
    HrFormDialog,
    HrIconHintBtn,
    HrMultiSelect,
    HrP,
    HrRadio,
    HrTextField,
  },

  props: {
    allTableViews: {
      type: Array,
      default: () => [],
    },

    canSaveGlobal: {
      type: Boolean,
      default: false,
    },

    saveMessage: {
      type: String,
      default: '',
    },

    currentView: {
      type: Object,
      default: () => ({}),
    },

    isEditing: {
      type: Boolean,
      default: false,
    },

    shareableRoles: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['hide', 'ok', 'tableViewDeleted'],

  data() {
    return {
      viewTitle: '',
      editedShareType: TABLE_VIEW_SHARED_TYPES.PRIVATE,
      selectedRoles: [],
      TABLE_VIEW_SHARED_TYPES,
    };
  },

  computed: {
    ...mapState(auth, [
      'userRoleList',
      'isManager',
      'userDetails',
    ]),

    saveMessageText() {
      return this.saveMessage || this.$t('tableViews.dialogSaveMessage');
    },

    userRoleOptions() {
      return this.userRoleList
        .filter((role) => !this.shareableRoles.length || this.shareableRoles.includes(role.role))
        .map((role) => ({
          label: role.label,
          value: role.value,
        }));
    },

    isExistingAsPrivate() {
      if (!this.currentView?.id) {
        return false;
      }

      const existingShareType = getExistingShareType(this.currentView?.user, this.currentView?.visibleToRoles);
      return existingShareType === TABLE_VIEW_SHARED_TYPES.PRIVATE;
    },

    currentVisibilityTitle() {
      switch (this.editedShareType) {
        case TABLE_VIEW_SHARED_TYPES.GLOBAL:
          return this.$t('tableViews.dialogGlobalLabel');
        case TABLE_VIEW_SHARED_TYPES.ROLE_SPECIFIC:
          return this.$t('tableViews.dialogSpecificLabel');
        default:
          return this.$t('tableViews.dialogPrivateLabel');
      }
    },

    canEditViewVisibility() {
      return !this.isEditing || this.currentView?.createdBy === this.userDetails.userId
                || this.isExistingAsPrivate || !this.currentView?.createdBy;
    },

    trimmedViewTitle() {
      return this.viewTitle?.trim() ?? '';
    },

    isSaveDisabled() {
      return !this.trimmedViewTitle
        || (this.editedShareType === TABLE_VIEW_SHARED_TYPES.ROLE_SPECIFIC && !this.selectedRoles?.length);
    },
  },

  methods: {
    show() {
      if (this.isEditing) {
        const { tableViewTitle, user, visibleToRoles } = this.currentView;

        this.viewTitle = tableViewTitle;
        this.editedShareType = getExistingShareType(user, visibleToRoles);
        this.selectedRoles = visibleToRoles ?? [];
      }
      // following method is REQUIRED, do not rename, it responsible for rendering dialogue on the screen
      this.$refs.dialog.show();
    }, // show()

    // following method is REQUIRED, do not rename, it hides the dialog from the screen
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    }, // hide()

    onDialogHide() {
      // required to be emitted, emits 'hide' event when QDialog is hidden
      this.$emit('hide');
    }, // onDialogHide()

    async onOkClick() {
      const isEditedToGlobal = this.editedShareType === TABLE_VIEW_SHARED_TYPES.GLOBAL;
      const isEditedToRoleSpecific = this.editedShareType === TABLE_VIEW_SHARED_TYPES.ROLE_SPECIFIC;
      const isEditedToPrivate = this.editedShareType === TABLE_VIEW_SHARED_TYPES.PRIVATE;

      // finds if a table view with the same name already exists
      // in the selected scope (“Your views,” or “Shared with you”)
      const sameTableViewName = this.allTableViews
        .filter(
          (tableView) => {
            const existingShareType = getExistingShareType(tableView.user, tableView?.visibleToRoles);

            return (
              ((isEditedToGlobal || isEditedToRoleSpecific)
                && (existingShareType === TABLE_VIEW_SHARED_TYPES.GLOBAL
                  || existingShareType === TABLE_VIEW_SHARED_TYPES.ROLE_SPECIFIC))
              || (isEditedToPrivate && existingShareType === TABLE_VIEW_SHARED_TYPES.PRIVATE)
            );
          },
        )
        .find(
          (tableView) => tableView.tableViewTitle.toLowerCase().trim() === this.trimmedViewTitle.toLowerCase()
            && this.currentView?.id !== tableView.id,
        );

      if (sameTableViewName) {
        alertDialog(
          this.$t('tableViews.dialogAlreadyExistsTitle'),
          this.$t('tableViews.dialogAlreadyExistsMessage'),
        );
        return;
      }

      this.$emit('ok', {
        id: this.isEditing ? this.currentView.id : null,
        title: this.trimmedViewTitle,
        visibleToRoles: isEditedToRoleSpecific ? this.selectedRoles : null,
        shareType: this.editedShareType,
        createdBy: this.currentView?.createdBy || this.userDetails.userId,

      });
      this.hide();
    }, // onOkClick()

    onDeleteClick() {
      const confirmMessage = this.isExistingAsPrivate
        ? this.$t('tableViews.confirmDelete', { title: this.currentView.tableViewTitle })
        : this.$t('tableViews.confirmDeleteGlobal', { title: this.currentView.tableViewTitle });
      confirmDestructiveDialog(
        this.$t('common.dialogTitleConfirm'),
        confirmMessage,
        () => {
          this.hide();
          this.$emit('tableViewDeleted', {
            id: this.currentView.id,
            tableViewSection: this.isExistingAsPrivate ? 'userTableViews' : 'clinicTableViews',
            tableViewTitle: this.currentView.tableViewTitle,
          });
        },
        { confirmLabel: this.$t('common.buttonDelete') },
      );
    }, // onDeleteClick()
  },
};
</script>
